import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { UserService } from 'src/app/shared/services/user.service';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { CreateAgendaItemDialogComponent } from '../create-agenda-item-dialog/create-agenda-item-dialog.component';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-view-agenda-item-dialog',
  templateUrl: './view-agenda-item-dialog.component.html',
  styleUrls: ['./view-agenda-item-dialog.component.scss']
})
export class ViewAgendaItemDialogComponent implements OnInit {
  agendaItem: any = null;
  isLoading: boolean = false;
  attachments: any[] = [];
  isSecretary: boolean = false;
  myNote: string = '';

  constructor(
    private dialogRef: MatDialogRef<ViewAgendaItemDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private apiService: ApiService,
    private loaderService: LoaderService,
    private userService: UserService,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    // Check if user is Secretary
    this.checkIfSecretary();
    
    const agendaItemId = this.data?.agendaItemId;
    if (agendaItemId) {
      await this.loadAgendaItemDetails(agendaItemId);
      await this.loadAttachments(agendaItemId);
      await this.loadMyNote(agendaItemId);

    } else {
      // If agenda item data is passed directly, use it
      this.agendaItem = this.data?.agendaItem || null;
      if (this.agendaItem?.id) {
        await this.loadAttachments(this.agendaItem.id);
        await this.loadMyNote(agendaItemId);
      }
    }
  }

  checkIfSecretary(): void {
    try {
      // Get user role
      let userRole = this.userService.user?.Role;
      
      // Fallback: Try to read role directly from token if not in user object
      if (!userRole && this.userService.token) {
        try {
          const token = this.userService.token;
          const payload = JSON.parse(atob(token.split('.')[1]));
          userRole = payload?.['Role'] || payload?.['role'] || payload?.[AppConstants.CLAIMS.Role];
        } catch (e) {
          console.warn('Could not decode token for role:', e);
        }
      }
      
      // Check if user is Secretary
      // Handle both string and numeric role values
      const roleValue = typeof userRole === 'string' && !isNaN(Number(userRole)) ? Number(userRole) : userRole;
      this.isSecretary = userRole === 'Secretary' || userRole === 'secretary' || userRole === 'SECRETARY' || 
                        userRole === 'Tender Committee Secretary' || roleValue === 4;
    } catch (error) {
      console.error('Error checking Secretary role:', error);
      this.isSecretary = false;
    }
  }

  openEditDialog(): void {
    if (!this.agendaItem?.id && !this.data?.agendaItemId) {
      console.error('Agenda item ID not found for editing.');
      return;
    }

    const agendaItemId = this.agendaItem?.id || this.data?.agendaItemId;

    // Close the current view dialog
    this.dialogRef.close();

    // Open the edit dialog
    this.dialog.open(CreateAgendaItemDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'create-agenda-item-dialog',
      data: {
        editMode: true,
        agendaItemId: agendaItemId,
        meetingId: this.data?.meetingId || null
      }
    });
  }

  async loadAgendaItemDetails(agendaItemId: number): Promise<void> {
    this.isLoading = true;
    this.loaderService.showLoader();

    try {
      const url = `${environment.API_URL}/api/AgendaItems/${agendaItemId}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));

      if (response?.isSuccess && response?.data) {
        this.agendaItem = response.data;
      } else {
        console.error('Failed to load agenda item:', response?.errorMessageEn);
      }
    } catch (error) {
      console.error('Error loading agenda item details:', error);
    } finally {
      this.isLoading = false;
      this.loaderService.hideLoader();
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  getSubject(): string {
    return this.agendaItem?.subject || this.agendaItem?.Subject || 'N/A';
  }

  getDescriptionEN(): string {
    return this.agendaItem?.agendaDescriptionEN || 
           this.agendaItem?.agendaDescriptionEn || 
           this.agendaItem?.AgendaDescriptionEN || 
           this.agendaItem?.descriptionEnglish || 
           'N/A';
  }

  getDescriptionAR(): string {
    return this.agendaItem?.agendaDescriptionAR || 
           this.agendaItem?.agendaDescriptionAr || 
           this.agendaItem?.AgendaDescriptionAR || 
           this.agendaItem?.descriptionArabic || 
           'N/A';
  }

  getRequiredFromTC(): string {
    return this.agendaItem?.requiredFromTC || 
           this.agendaItem?.RequiredFromTC || 
           this.agendaItem?.requiredFromTenderCommittee || 
           'N/A';
  }

  getTCNotes(): string {
    return this.agendaItem?.tcNotes || 
           this.agendaItem?.TCNotes || 
           'N/A';
  }

  async loadMyNote(agendaItemId: number): Promise<void> {
    this.isLoading = true;

    try {
      const url = `${environment.API_URL}/api/AgendaItems/${agendaItemId}/MyNote`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));

      if (typeof response === 'string') {
        this.myNote = response;
      } else if (typeof response?.data === 'string') {
        this.myNote = response.data;
      } else if (typeof response?.notes === 'string') {
        this.myNote = response.notes;
      } else {
        this.myNote = '';
      }
    } catch (error) {
      console.error('Error loading my note:', error);
      this.myNote = '';
    } finally {
      this.isLoading = false;
    }
  }

  async saveMyNote(): Promise<void> {
    const agendaItemId = this.agendaItem?.id || this.data?.agendaItemId;
    if (!agendaItemId) {
      console.error('Agenda item ID not found for saving note.');
      return;
    }

    this.isLoading = true;

    try {
      const url = `${environment.API_URL}/api/AgendaItems/${agendaItemId}/MyNote`;
      const payload = {
        notes: this.myNote || ''
      };

      const response: any = await lastValueFrom(this.apiService.putByUrl(url, payload));

      if (response?.isSuccess === false) {
        console.error('Failed to save my note:', response?.errorMessageEn);
        return;
      }

      this.dialogRef.close(true);

    } catch (error) {
      console.error('Error saving my note:', error);
    } finally {
      this.isLoading = false;
    }
  }
  
  async loadAttachments(agendaItemId: number): Promise<void> {
    try {
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: agendaItemId.toString(),
        processName: 'AgendaItem',
        processID: agendaItemId.toString()
      };

      const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

      if (documentsResponse?.isSuccess && documentsResponse?.data) {
        if (Array.isArray(documentsResponse.data) && documentsResponse.data.length > 0) {
          this.attachments = documentsResponse.data.map((doc: any) => {
            // Use the original filename as-is (what user uploaded)
            const originalFileName = doc.name || 'document';

            return {
              fileName: originalFileName, // Original filename for download
              name: originalFileName, // Display name (original)
              fileSize: doc.fileSize || doc.size,
              attachmentId: doc.attachmentId,
              fileContent: doc.fileContent, // SAS URL for direct download
              mimeType: doc.mimeType || doc.contentType
            };
          });
        } else {
          this.attachments = [];
        }
      } else {
        this.attachments = [];
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
      this.attachments = [];
    }
  }

  getAttachments(): any[] {
    return this.attachments || [];
  }

  getAttachmentUrl(attachment: any): string {
    // Use direct blob access (SAS URL) if available, otherwise use backend proxy
    if (attachment.fileContent) {
      return attachment.fileContent;
    } else if (attachment.attachmentId) {
      return `${environment.API_URL}/api/Documents/DownloadDocument/${attachment.attachmentId}`;
    }
    return '#';
  }

  async viewAttachment(attachment: any): Promise<void> {
    try {
      const fileUrl = this.getAttachmentUrl(attachment);
      if (!fileUrl || fileUrl === '#') {
        console.error('Invalid attachment URL');
        return;
      }

      // Detect Safari/iPad - Safari blocks window.open() after async operations
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                       /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // For Safari/iPad: Open the original URL directly in a new tab (no blank page)
      if (isSafari) {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      // For Chrome/other browsers: Fetch as blob to prevent downloads
      const response = await fetch(fileUrl, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const file = new File([blob], attachment.name, { type: attachment.mimeType || 'application/octet-stream' });
      const fileURL = URL.createObjectURL(file);
      
      // Open in new tab with file loaded
      window.open(fileURL, '_blank', 'noopener,noreferrer');
      
      // Don't revoke the blob URL - let browser handle cleanup when tab closes
    } catch (error) {
      console.error('Error viewing attachment:', error);
      // Fallback to direct URL
      window.open(this.getAttachmentUrl(attachment), '_blank', 'noopener,noreferrer');
    }
  }

  async downloadAttachment(attachment: any): Promise<void> {
    try {
      const fileUrl = this.getAttachmentUrl(attachment);
      if (!fileUrl || fileUrl === '#') {
        console.error('Invalid attachment URL');
        return;
      }

      // Fetch the file as a blob
      const response = await fetch(fileUrl, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Use the original filename that was uploaded (what's displayed)
      const downloadFileName = attachment.name || attachment.fileName || 'document';
      
      // Create a File object with the original filename
      const file = new File([blob], downloadFileName, { type: attachment.mimeType || 'application/octet-stream' });
      
      // Create object URL and trigger download with original filename
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = downloadFileName; // Use original filename (same as displayed)
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Revoke the object URL after a short delay to allow download to start
      setTimeout(() => {
        URL.revokeObjectURL(fileURL);
      }, 100);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      // Fallback to direct download
      const link = document.createElement('a');
      link.href = this.getAttachmentUrl(attachment);
      link.download = attachment.name || attachment.fileName || 'document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}
